<!--
// app/pages/[region]/stats/assists.vue
-->
<script setup lang="ts">
import type { GroupedRanking } from "~~/src/stats/domain/models/Rankings";
import type { RankingsData } from "~~/src/player/domain/Player";
import { useSeasonFilter } from "~/composables/season/seasonFilter";
import { useSeasons } from "~/composables/season/seasons";
import { setAliasUrl } from "~~/src/Shared/utils";
import statsService from "~~/src/stats/domain/services/statsService";
import BaseBackdropLoading from "~/components/base/BaseBackdropLoading.vue";
import HeaderBannerTitle from "~/components/Shared/HeaderBannerTitle.vue";
import SeasonsFilter from "~/components/Shared/SeasonsFilter.vue";
import SidebarMenuStats from "~/components/pages/region-stats/SidebarMenuStats.vue";
import PrimeDataTable from "primevue/datatable";
import PrimeColumn from "primevue/column";
import { $SEASONS_WITHOUT_DATA } from "~~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import { useNuxtApp } from "#imports";

const { $t, $localeRoute } = useNuxtApp();

definePageMeta({
  middleware: [
    "not-found-americas",
    "not-found-world-cup",
    "not-found-italy",
    "not-found-queens-americas",
    "not-found-nations",
    "not-found-king-of-kings-nations",
    "not-found-brazil",
    "not-found-france",
    "not-found-germany",
    "not-found-mena",
  ],
});

const { getSeasonParameterStats } = useSeasons();
const { mergeRankingsByPlayerId } = statsService();
const { seasonId, seasonClusterId } = useSeasonFilter();
const router = useRouter();
const tableStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);
const seasonAssistants = ref<RankingsData[]>();
const matchesPlayed = ref<RankingsData[]>();
const errorMessage = ref<boolean>(false);
const tableData = ref<GroupedRanking[] | undefined>([]);

const getSeasonData = async (): Promise<void> => {
  if (!!seasonId.value && $SEASONS_WITHOUT_DATA.includes(Number(seasonId.value))) {
    try {
      loadingSeasonData.value = true;
      errorMessage.value = false;

      const seasonIdValue = seasonId.value;

      seasonAssistants.value = seasonIdValue
        ? await getSeasonParameterStats(seasonIdValue, "ASS-V")
        : undefined;

      matchesPlayed.value = seasonIdValue ? await getSeasonParameterStats(seasonIdValue, "PG") : undefined;

      const _seasonAssistants = seasonAssistants.value;
      const _matchesPlayed = matchesPlayed.value;
      if (_seasonAssistants && _matchesPlayed) {
        tableData.value = mergeRankingsByPlayerId(_seasonAssistants, [_matchesPlayed]);
      }
    } catch (e) {
      errorMessage.value = true;
    } finally {
      loadingSeasonData.value = false;
      tableStatsKey.value++;
      sidebarKey.value++;
    }
  } else {
    await nextTick();
    await router.push(
      $localeRoute({ name: "region-stats", query: { season: seasonClusterId.value, split: seasonId.value } })
    );
  }
};

useSeoMeta({
  title: $t("menuItem.stats") + " " + $t("menuItem.statsPlayers") + " - " + $t("stats.sidebar.menu.assists"),
  ogTitle:
    $t("menuItem.stats") + " " + $t("menuItem.statsPlayers") + " - " + $t("stats.sidebar.menu.assists"),
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('menuItem.statsPlayers')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div class="kql-w-container">
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
      />
    </div>
  </section>

  <section class="px-4 md:px-6 my-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0 relative">
        <sidebar-menu-stats
          :is-blocked="seasonId === 3"
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3 relative">
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

        <div v-if="errorMessage || (tableData && !tableData.length)">
          {{ $t("stats.message.notAvailable") }}
        </div>

        <div class="stats-table-data max-w-full overflow-hidden" v-if="tableData && tableData.length">
          <PrimeDataTable :value="tableData" scrollable scrollHeight="540px">
            <PrimeColumn
              field="rankingData"
              header="Ranking"
              frozen
              class="ranking max-w-[120px] min-w-[75px]"
              style="max-width: 120px; min-width: 75px"
              ><template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[0]?.ranking ? slotProps.data?.rankingData[0]?.ranking : 0
                }}</span></template
              >
            </PrimeColumn>
            <PrimeColumn
              field="player.shortName"
              :header="$t('cards.stats.name.text')"
              class="player-name max-w-[30%] !min-w-[20%] leading-1 md:!min-w-[200px]"
              style="min-width: 20%; line-height: 1.1; max-width: 30%"
              frozen
            ></PrimeColumn>
            <PrimeColumn
              field="team"
              :header="$t('tables.team.word')"
              class="team pe-2 md:pe-0 max-w-[80px] min-w-[50px]"
              style="max-width: 80px; min-width: 50px"
            >
              <template #body="slotProps">
                <NuxtImg
                  v-if="slotProps && slotProps.data?.team"
                  loading="lazy"
                  :src="setAliasUrl(slotProps.data.team.logo.url)"
                  :alt="slotProps.data.team.completeName"
                  class="team-logo"
                  width="48"
                  height="48"
                  sizes="40px sm:48px"
                />
              </template>
            </PrimeColumn>
            <PrimeColumn field="0" style="max-width: 500px; min-width: 40px; width: 80%"></PrimeColumn>
            <PrimeColumn
              field="rankingData"
              :header="$t('tables.gamesPlayed.ab')"
              class="text-center data-column max-w-[60px] min-w-[45px]"
              style="min-width: 45px; max-width: 60px"
            >
              <template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[1]?.total ? slotProps.data?.rankingData[1]?.total : 0
                }}</span></template
              >
            </PrimeColumn>
            <PrimeColumn
              field="rankingData"
              :header="$t('general.stats.assists')"
              class="text-center data-column max-w-[60px] min-w-[45px] pe-4"
              style="min-width: 45px; max-width: 60px"
              ><template #body="slotProps"
                ><span v-if="slotProps && slotProps.data">{{
                  slotProps.data?.rankingData[0]?.total ? slotProps.data?.rankingData[0]?.total : 0
                }}</span></template
              >
            </PrimeColumn>
          </PrimeDataTable>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
